/**
 * Bundle Constructor Component
 * Product grid add-to-cart, quantity controls, progress bar sync.
 * Reuses cart drawer (renderContents) and window.updateBundleProgressFromCart for progress.
 */

class BundleConstructor {
  constructor() {
    this.section = document.querySelector('.bundle-constructor[data-section-id]')
      || document.querySelector('[data-bundle-tray-host]');

    this.ROOT = window.Shopify?.routes?.root || '/';
    this.routes = window.Shopify?.routes || window.routes || {};
    this.BUNDLE_CART_UPDATED_EVENT = 'bundle:cart-updated';
    this.DESKTOP_BREAKPOINT_PX = 990;
    this.SCROLL_ACTIVE_THRESHOLD = 0.5;
    this.SWIPE_UP_MIN_PX = 50;
    this.SWIPE_MAX_DURATION_MS = 400;
    this.PROGRESS_WRAP_MIN_HEIGHT_PX = 132;
    this.scrollThrottleId = null;
    this.scrollActiveShown = false;
    this.scrollExpandAnimating = false;

    this.eventSeq = 0;
    this.lastHandledExternalEventId = null;
    this.externalSyncTimer = null;

    this.lastPaidCount = null;
    this.trayAutoShowSuppressed = false;
    this.AUTO_PROGRESS_AFTER_OPEN_DELAY_MS = 125;
    this.DEBUG = false;

    this.init();
  }

  init() {
    if (!this.section) {
      console.warn('Bundle constructor section not found');
      return;
    }

    this.getCart().then((cart) => {
      this.syncSectionUiFromCart(cart);
    });

    this.bindSection();
    this.bindProgressWrapActions();
    this.mountProgressWrapToBody();
    this.bindScrollActive();
    this.updateScrollActiveState();
    this.initPubSubListeners();
    this.initTrayCloseListener();

    // console.log('BundleConstructor initialized');
  }

  /**
   * Cart API
   */

  getCards() {
    return this.section ? this.section.querySelectorAll('.bundle-constructor-card[data-variant-id]') : [];
  }

  getCart() {
    return fetch(`${this.ROOT}cart.js`, { credentials: 'same-origin' })
      .then((r) => r.json())
      .catch(() => ({ items: [] }));
  }

  paidItemCount(cart) {
    if (!cart?.items?.length) return 0;
    return cart.items.reduce((sum, item) => sum + (item.final_price > 0 ? (item.quantity || 1) : 0), 0);
  }

  changeCartByVariantId(variantId, quantity) {
    const url = this.routes.cart_change_url || `${this.ROOT}cart/change.js`;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: String(variantId), quantity: Math.max(0, quantity) }),
    }).then((r) => r.json());
  }

  cleanupFreeItemsIfPaidBelowTwo(cart) {
    if (!cart?.items?.length) return Promise.resolve(cart);
    if (this.paidItemCount(cart) >= 2) return Promise.resolve(cart);
    const freeVariantIds = [...new Set(
      cart.items
        .filter((item) => (item.final_price || 0) <= 0)
        .map((item) => String(item.variant_id))
        .filter(Boolean)
    )];
    if (!freeVariantIds.length) return Promise.resolve(cart);
    let chain = Promise.resolve();
    freeVariantIds.forEach((variantId) => {
      chain = chain.then(() => this.changeCartByVariantId(variantId, 0));
    });
    return chain.then(() => this.getCart());
  }

  addToCart(variantId) {
    const cartDrawer = document.querySelector('cart-drawer');
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    if (cartDrawer && cartDrawer.getSectionsToRender) {
      const ids = cartDrawer.getSectionsToRender().map((s) => s.id);
      formData.append('sections', ids.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    const url = this.routes.cart_add_url || `${this.ROOT}cart/add`;
    return fetch(url, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      credentials: 'same-origin',
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && data.status !== 200) {
          throw new Error(data.description || data.message || 'Add failed');
        }
        if (cartDrawer && data.sections && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(data, { open: false });
        }
        return this.getCart().then((cart) => {
          return this.cleanupFreeItemsIfPaidBelowTwo(cart).then((finalCart) => {
            this.syncSectionUiFromCart(finalCart);
            this.emitCartUpdated(finalCart);
            return finalCart;
          });
        });
      });
  }

  changeCart(variantId, quantity) {
    const url = this.routes.cart_change_url || `${this.ROOT}cart/change.js`;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: String(variantId), quantity: Math.max(0, quantity) }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status && data.status !== 200) throw new Error(data.message || 'Change failed');
        return this.getCart().then((cart) => {
          return this.cleanupFreeItemsIfPaidBelowTwo(cart).then((finalCart) => {
            this.syncSectionUiFromCart(finalCart);
            this.emitCartUpdated(finalCart);
            return finalCart;
          });
        });
      });
  }

  /**
   * UI Sync
   */

  syncCardsFromCart(cart) {
    const cards = this.getCards();
    const byVariant = new Map();
    if (cart?.items) {
      cart.items.forEach((item) => {
        const id = String(item.variant_id);
        byVariant.set(id, (byVariant.get(id) || 0) + (item.quantity || 1));
      });
    }
    cards.forEach((card) => {
      const variantId = card.dataset.variantId;
      const qty = byVariant.get(variantId) || 0;
      const addWrap = card.querySelector('[data-add-wrap]');
      const qtyWrap = card.querySelector('[data-quantity-wrap]');
      const qtyValue = card.querySelector('[data-quantity-value]');
      if (qty > 0) {
        if (addWrap) addWrap.classList.add('hidden');
        if (qtyWrap) {
          qtyWrap.classList.add('visible');
          qtyWrap.setAttribute('aria-hidden', 'false');
        }
        if (qtyValue) qtyValue.textContent = qty;
        card.classList.add('bundle-constructor-card--in-cart');
      } else {
        if (addWrap) addWrap.classList.remove('hidden');
        if (qtyWrap) {
          qtyWrap.classList.remove('visible');
          qtyWrap.setAttribute('aria-hidden', 'true');
        }
        if (qtyValue) qtyValue.textContent = '0';
        card.classList.remove('bundle-constructor-card--in-cart');
      }
    });
  }

  log(action, extra = {}) {
    if (!this.DEBUG) return;
    console.log(`[BundleConstructor] ${action}`, extra);
  }

  updateProgress(cart, opts) {
    this.log('updateProgress', { hasOpts: !!opts, forceAnimate: opts?.forceAnimate });
    if (typeof window.updateBundleProgressFromCart === 'function') {
      window.updateBundleProgressFromCart(cart, opts);
    }
  }

  syncProgressWrapCartState(cart) {
    const hasItems = (cart?.item_count || 0) > 0;
    document.querySelectorAll('.bundle-constructor__progress-wrap').forEach((wrap) => {
      wrap.classList.toggle('has-cart-items', hasItems);
    });
  }

  syncCardsAndWrapFromCart(cart) {
    this.syncCardsFromCart(cart);
    this.syncProgressWrapCartState(cart);
  }

  syncSectionUiFromCart(cart) {
    this.syncCardsAndWrapFromCart(cart);
    const trayShown = this.checkAutoShowTray(cart);
    const dismissed = typeof window.getBundleTrayDismissed === 'function' && window.getBundleTrayDismissed();
    this.log('syncSectionUiFromCart', { trayShown, dismissed, delayMs: this.AUTO_PROGRESS_AFTER_OPEN_DELAY_MS });
    if (trayShown && !dismissed) {
      this.log('syncSectionUiFromCart:auto-show-path', { progressDelayMs: this.AUTO_PROGRESS_AFTER_OPEN_DELAY_MS });
      if (typeof window.showViewBundlePopup === 'function') window.showViewBundlePopup({ fromAutoShow: true, progressDelayMs: this.AUTO_PROGRESS_AFTER_OPEN_DELAY_MS });
      setTimeout(() => {
        this.log('syncSectionUiFromCart:delayed-updateProgress-fire', { forceAnimate: true });
        this.updateProgress(cart, { forceAnimate: true });
      }, this.AUTO_PROGRESS_AFTER_OPEN_DELAY_MS);
    } else {
      this.log('syncSectionUiFromCart:immediate-updateProgress');
      this.updateProgress(cart);
    }
  }

  /**
   * Auto-show Tray
   */

  checkAutoShowTray(cart) {
    const newPaidCount = this.paidItemCount(cart);
    const prevPaidCount = this.lastPaidCount;
    this.lastPaidCount = newPaidCount;

    if (newPaidCount <= 1) {
      this.trayAutoShowSuppressed = false;
    }

    if (prevPaidCount === null) {
      this.log('checkAutoShowTray', { prevPaidCount, newPaidCount, result: false, reason: 'first-load' });
      return false;
    }

    const shouldShow = newPaidCount >= 2 && prevPaidCount < 2 && !this.trayAutoShowSuppressed;
    this.log('checkAutoShowTray', { prevPaidCount, newPaidCount, suppressed: this.trayAutoShowSuppressed, result: shouldShow });
    return shouldShow;
  }

  /**
   * Events & PubSub
   */

  emitCartUpdated(cartData) {
    const payload = {
      source: 'bundle-constructor',
      cartData: cartData && cartData.items ? cartData : null,
      eventId: `bundle-constructor-${Date.now()}-${++this.eventSeq}`,
    };
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS?.cartUpdate) {
      Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, payload)).catch(() => {});
    }
    document.dispatchEvent(new CustomEvent(this.BUNDLE_CART_UPDATED_EVENT, { detail: payload }));
  }

  syncFromExternalCartUpdate(payload = {}) {
    if (!this.section) return;
    const eventId = payload?.eventId;
    if (eventId && eventId === this.lastHandledExternalEventId) return;
    if (eventId) this.lastHandledExternalEventId = eventId;
    if (payload?.source === 'bundle-constructor') return;
    this.log('syncFromExternalCartUpdate:scheduled', { source: payload?.source, eventId });
    clearTimeout(this.externalSyncTimer);
    this.externalSyncTimer = setTimeout(() => {
      const source = String(payload?.source || '');
      const skipProgressAnimation = source.startsWith('bundle-drawer:');
      this.log('syncFromExternalCartUpdate:run', { source, skipProgressAnimation, hasCartData: !!payload?.cartData?.items });
      if (payload?.cartData?.items) {
        if (skipProgressAnimation) {
          this.syncCardsAndWrapFromCart(payload.cartData);
          if (this.needsDrawerProgressSync()) {
            this.updateProgress(payload.cartData);
          }
          const trayShown = this.checkAutoShowTray(payload.cartData);
          this.log('syncFromExternalCartUpdate:skipAnim-with-data', { trayShown });
          if (trayShown && !(typeof window.getBundleTrayDismissed === 'function' && window.getBundleTrayDismissed())) {
            if (typeof window.showViewBundlePopup === 'function') window.showViewBundlePopup({ fromAutoShow: true, progressDelayMs: this.AUTO_PROGRESS_AFTER_OPEN_DELAY_MS });
            setTimeout(() => this.updateProgress(payload.cartData, { forceAnimate: true }), this.AUTO_PROGRESS_AFTER_OPEN_DELAY_MS);
          }
        } else {
          this.syncSectionUiFromCart(payload.cartData);
        }
        return;
      }
      this.getCart().then((cart) => {
        if (skipProgressAnimation) {
          this.syncCardsAndWrapFromCart(cart);
          if (this.needsDrawerProgressSync()) {
            this.updateProgress(cart);
          }
          const trayShown = this.checkAutoShowTray(cart);
          this.log('syncFromExternalCartUpdate:skipAnim-fetched', { trayShown });
          if (trayShown && !(typeof window.getBundleTrayDismissed === 'function' && window.getBundleTrayDismissed())) {
            if (typeof window.showViewBundlePopup === 'function') window.showViewBundlePopup({ fromAutoShow: true, progressDelayMs: this.AUTO_PROGRESS_AFTER_OPEN_DELAY_MS });
            setTimeout(() => this.updateProgress(cart, { forceAnimate: true }), this.AUTO_PROGRESS_AFTER_OPEN_DELAY_MS);
          }
        } else {
          this.syncSectionUiFromCart(cart);
        }
      });
    }, 140);
  }

  initTrayCloseListener() {
    document.addEventListener('click', (e) => {
      if (e.target.closest('.view-bundle-popup__btn--keep-shopping') || e.target.closest('.view-bundle-popup__overlay')) {
        this.trayAutoShowSuppressed = true;
      }
    });
  }

  initPubSubListeners() {
    if (this.section.dataset.bundleSyncBound === '1') return;
    this.section.dataset.bundleSyncBound = '1';

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS?.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
        this.syncFromExternalCartUpdate(payload || {});
      });
    }
    document.addEventListener(this.BUNDLE_CART_UPDATED_EVENT, (event) => {
      this.syncFromExternalCartUpdate(event?.detail || {});
    });
  }

  /**
   * Section Binding
   */

  bindSection() {
    const cards = this.getCards();
    cards.forEach((card) => {
      const variantId = card.dataset.variantId;
      const addBtn = card.querySelector('[data-add-to-cart]');
      const qtyMinus = card.querySelector('[data-qty-minus]');
      const qtyPlus = card.querySelector('[data-qty-plus]');
      const qtyValue = card.querySelector('[data-quantity-value]');

      if (addBtn && !addBtn.dataset.bound) {
        addBtn.dataset.bound = '1';
        addBtn.addEventListener('click', () => {
          if (addBtn.disabled || addBtn.classList.contains('loading')) return;
          addBtn.classList.add('loading');
          addBtn.disabled = true;
          this.addToCart(variantId)
            .then(() => {})
            .catch((err) => console.error('[BundleConstructor] Add error:', err))
            .finally(() => {
              addBtn.classList.remove('loading');
              addBtn.disabled = false;
            });
        });
      }

      const qtyWrap = card.querySelector('[data-quantity-wrap]');
      const setQuantityLoading = (loading) => {
        if (qtyWrap) qtyWrap.classList.toggle('is-loading', loading);
        if (qtyMinus) qtyMinus.disabled = loading;
        if (qtyPlus) qtyPlus.disabled = loading;
      };

      const updateQty = (delta) => {
        const current = parseInt(qtyValue?.textContent || '0', 10);
        const next = Math.max(0, current + delta);
        if (next === current) return;
        setQuantityLoading(true);
        this.changeCart(variantId, next)
          .catch((err) => console.error('[BundleConstructor] Change error:', err))
          .finally(() => setQuantityLoading(false));
      };

      if (qtyMinus && !qtyMinus.dataset.bound) {
        qtyMinus.dataset.bound = '1';
        qtyMinus.addEventListener('click', () => updateQty(-1));
      }
      if (qtyPlus && !qtyPlus.dataset.bound) {
        qtyPlus.dataset.bound = '1';
        qtyPlus.addEventListener('click', () => updateQty(1));
      }
    });
  }

  bindProgressWrapActions() {
    const progressWrap = this.section.querySelector('.bundle-constructor__progress-wrap');
    if (!progressWrap) return;
    const viewBundleBtn = progressWrap.querySelector('.bundle-constructor__progress-wrap-action-button--view-bundle');
    const checkoutBtn = progressWrap.querySelector('.bundle-constructor__progress-wrap-action-button--checkout');
    const openViewBundlePopup = () => {
      if (typeof window.showViewBundlePopup === 'function') window.showViewBundlePopup();
    };
    const closeViewBundlePopup = () => {
      if (typeof window.cartDrawerBundle?.closeViewBundlePopup === 'function') window.cartDrawerBundle.closeViewBundlePopup();
    };
    if (viewBundleBtn && !viewBundleBtn.dataset.bound) {
      viewBundleBtn.dataset.bound = '1';
      viewBundleBtn.addEventListener('click', openViewBundlePopup);
    }
    if (checkoutBtn && !checkoutBtn.dataset.bound) {
      checkoutBtn.dataset.bound = '1';
      checkoutBtn.addEventListener('click', openViewBundlePopup);
    }
    // Tablet/mobile: drag to resize block height (min/max); desktop unchanged (skip inline minimal tray)
    if (
      !progressWrap.classList.contains('bundle-constructor__progress-wrap--minimal')
      && !progressWrap.dataset.blockSwipeBound
    ) {
      progressWrap.dataset.blockSwipeBound = '1';
      const TRAY_REVEAL_DRAG_PX = 8;
      const getMinHeightPx = () => {
        const v = getComputedStyle(progressWrap).getPropertyValue('--bundle-progress-wrap-min-height');
        const parsed = v ? parseInt(v, 10) : NaN;
        return Number.isFinite(parsed) ? parsed : this.PROGRESS_WRAP_MIN_HEIGHT_PX;
      };
      const getMaxHeightPx = () => {
        const prevHeight = progressWrap.style.height;
        progressWrap.style.height = '';
        const full = progressWrap.scrollHeight;
        progressWrap.style.height = prevHeight;
        const cap = Math.min(full, window.innerHeight * 0.9);
        return Math.max(cap, getMinHeightPx());
      };
      const revealTrayIfNeeded = () => {
        if (this._trayRevealFromGesture) return;
        const panel = document.querySelector('[data-bundle-tray-panel]');
        if (!panel?.classList.contains('hidden')) return;
        this._trayRevealFromGesture = true;
        if (typeof window.showViewBundlePopup === 'function') window.showViewBundlePopup();
      };
      progressWrap.addEventListener('touchstart', (e) => {
        if (window.innerWidth >= this.DESKTOP_BREAKPOINT_PX) return;
        const t = e.changedTouches?.[0] || e.touches?.[0];
        if (!t) return;
        const minPx = getMinHeightPx();
        const maxPx = getMaxHeightPx();
        const rect = progressWrap.getBoundingClientRect();
        const currentHeight = progressWrap.style.height ? parseFloat(progressWrap.style.height) : rect.height;
        const startHeight = Number.isFinite(currentHeight) && currentHeight > 0 ? currentHeight : rect.height;
        this._touchStartY = t.clientY;
        this._touchStartX = t.clientX;
        this._touchId = t.identifier;
        this._swipeStartHeight = Math.min(maxPx, Math.max(minPx, startHeight));
        this._swipeMinPx = minPx;
        this._swipeMaxPx = maxPx;
        this._trayRevealFromGesture = false;
      }, { passive: true });
      progressWrap.addEventListener('touchmove', (e) => {
        if (window.innerWidth >= this.DESKTOP_BREAKPOINT_PX) return;
        if (this._touchStartY == null || this._swipeStartHeight == null) return;
        const t = e.touches && [...e.touches].find((tou) => tou.identifier === this._touchId);
        if (!t) return;
        e.preventDefault();
        const deltaY = this._touchStartY - t.clientY;
        const newHeight = this._swipeStartHeight + deltaY;
        const clamped = Math.min(this._swipeMaxPx, Math.max(this._swipeMinPx, newHeight));
        progressWrap.style.transition = 'none';
        progressWrap.style.overflow = 'hidden';
        progressWrap.style.height = `${clamped}px`;
        if (clamped > this._swipeMinPx + TRAY_REVEAL_DRAG_PX) revealTrayIfNeeded();
      }, { passive: false });
      progressWrap.addEventListener('touchend', (e) => {
        if (window.innerWidth >= this.DESKTOP_BREAKPOINT_PX) return;
        if (this._touchStartY == null) return;
        const t = e.changedTouches?.[0];
        if (!t) return;
        const minPx = getMinHeightPx();
        const currentH = progressWrap.style.height ? parseFloat(progressWrap.style.height) : NaN;
        if (Number.isFinite(currentH) && currentH > minPx + 2) revealTrayIfNeeded();
        if (Number.isFinite(currentH) && currentH <= minPx + 2 && typeof window.setBundleTrayDismissed === 'function') {
          window.setBundleTrayDismissed();
        }
        this._touchStartY = null;
        this._touchId = null;
        this._swipeStartHeight = null;
      }, { passive: true });
      progressWrap.addEventListener('touchcancel', () => {
        if (this._touchStartY != null) {
          this._touchStartY = null;
          this._touchId = null;
          this._swipeStartHeight = null;
        }
      }, { passive: true });
    }
  }

  mountProgressWrapToBody() {
    if (this.section?.hasAttribute?.('data-goes-well-with')) return;
    const progressWrap = this.section.querySelector('.bundle-constructor__progress-wrap');
    if (!progressWrap) return;
    if (progressWrap.dataset.movedToBody === '1') return;
    progressWrap.dataset.movedToBody = '1';
    document.body.appendChild(progressWrap);
  }

  /** Progress bar used for scroll-active (fixed bundle builder only; not inline Goes Well With tray). */
  getScrollActiveProgressWrap() {
    return Array.from(document.querySelectorAll('.bundle-constructor__progress-wrap')).find(
      (el) => !el.closest('[data-goes-well-with]')
    );
  }

  /** Inline bundle tray under Goes Well With carousel — must sync via updateProgress when cart changes from drawer. */
  hasInlineGoesWellTray() {
    return Boolean(document.querySelector('[data-goes-well-with][data-bundle-tray-host] .bundle-constructor__progress-wrap'));
  }

  /** Bundle constructor page (not GWW): progress on page; drawer +/- must still run updateBundleProgressFromCart. */
  needsDrawerProgressSync() {
    if (this.hasInlineGoesWellTray()) return true;
    if (!this.section?.classList?.contains('bundle-constructor')) return false;
    // Progress wrap may be under section or moved to body (see mountProgressWrapToBody).
    return Boolean(
      this.getScrollActiveProgressWrap() || this.section.querySelector('.bundle-constructor__progress-wrap')
    );
  }

  /**
   * Animate progress-wrap height from current to max over durationMs (e.g. 0.3s like swipe).
   */
  animateProgressWrapToMaxHeight(progressWrap, durationMs = 300) {
    if (this.scrollExpandAnimating) return;
    const startHeight = progressWrap.getBoundingClientRect().height;
    // Use 'auto' so CSS height:132px is overridden and we measure real expanded height
    progressWrap.style.height = 'auto';
    progressWrap.style.overflow = '';
    const targetHeight = progressWrap.getBoundingClientRect().height;
    progressWrap.style.height = `${startHeight}px`;
    progressWrap.style.overflow = 'hidden';
    const cappedHeight = Math.min(targetHeight, window.innerHeight * 0.9);
    this.scrollExpandAnimating = true;
    progressWrap.style.transition = `height ${durationMs}ms ease-out`;
    requestAnimationFrame(() => {
      progressWrap.style.height = `${cappedHeight}px`;
    });
    clearTimeout(this.scrollExpandTimer);
    this.scrollExpandTimer = setTimeout(() => {
      this.scrollExpandTimer = null;
      this.scrollExpandAnimating = false;
      progressWrap.style.transition = '';
      progressWrap.style.overflow = '';
      // Keep height at cappedHeight so CSS height:132px does not collapse the block again
    }, durationMs);
  }

  /**
   * Scroll-active: at 50% scroll add is-scroll-active and open tray (so block gets height/full content on all viewports).
   */
  updateScrollActiveState() {
    if (typeof window.getBundleTrayDismissed === 'function' && window.getBundleTrayDismissed()) return;
    const progressWrap = this.getScrollActiveProgressWrap();
    if (!progressWrap) return;
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    const scrollRatio = maxScroll > 0 ? window.scrollY / maxScroll : 0;
    const isActive = scrollRatio >= this.SCROLL_ACTIVE_THRESHOLD;
    const wasActive = progressWrap.classList.contains('is-scroll-active');
    const wasTrayOpen = progressWrap.classList.contains('is-tray-open');
    if (isActive && wasTrayOpen) {
      this.animateProgressWrapToMaxHeight(progressWrap, 300);
    }
    if (isActive && wasActive || wasTrayOpen) return;
    progressWrap.classList.toggle('is-scroll-active', isActive);
    if (isActive && !wasActive) {
      this.scrollActiveShown = true;
      if (typeof window.showViewBundlePopup === 'function') window.showViewBundlePopup();
      this.animateProgressWrapToMaxHeight(progressWrap, 300);
    }
    if (!isActive) this.scrollActiveShown = false;
  }

  bindScrollActive() {
    const progressWrap = this.getScrollActiveProgressWrap();
    if (!progressWrap || progressWrap.dataset.scrollActiveBound === '1') return;
    progressWrap.dataset.scrollActiveBound = '1';
    const throttled = () => {
      if (this.scrollThrottleId != null) return;
      this.scrollThrottleId = window.requestAnimationFrame(() => {
        this.scrollThrottleId = null;
        this.updateScrollActiveState();
      });
    };
    window.addEventListener('scroll', throttled, { passive: true });
  }

  /**
   * Cleanup
   */

  destroy() {
    clearTimeout(this.externalSyncTimer);
    clearTimeout(this.scrollExpandTimer);
    if (this.scrollThrottleId != null) window.cancelAnimationFrame(this.scrollThrottleId);
  }
}

// Initialize (single instance — script may load from bundle-constructor and goes-well-with sections)
function initBundleConstructorOnce() {
  if (window.bundleConstructor) return;
  window.bundleConstructor = new BundleConstructor();
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBundleConstructorOnce);
} else {
  initBundleConstructorOnce();
}
